import type { Catalog } from "../../en";

/** Filipino: Analytics screen (mirrors en/engage/analytics.ts). */
export const analytics: Catalog["engage"]["analytics"] = {
  title: "Analytics",
  subtitle: "Paano tumutulong ang assistant sa mga bisita at nakakatipid ng oras ng staff.",
  period: "{{start}} – {{end}}",
  filters: {
    range: "Saklaw ng petsa",
    custom: "Custom",
    from: "Mula",
    to: "Hanggang",
    agent: "Agent",
    channel: "Channel",
    invalidRange: "Pumili ng simula na hindi lalampas sa katapusan (hanggang 366 araw).",
  },
  export: {
    menu: "I-export",
    csv: "CSV (raw na daily data)",
    done: "Na-export · {{file}}",
  },
  kpi: {
    conversations: "Mga conversation",
    uniqueUsers: "Natatanging user",
    messages: "Mga mensahe",
    aiResolution: "AI resolution rate",
    handoffRate: "Handoff rate",
    avgResponse: "Avg na bilis ng sagot",
    csat: "CSAT",
    hoursSaved: "Tantyang oras na natipid ng staff",
    vsPrevious: "{{value}} kumpara sa nakaraang period",
    pts: "{{value}} pts",
    distinctVisitors: "Magkakaibang bisita",
    perConversation: "{{value}} bawat conversation",
    p95: "p95 {{value}}",
    ratings: "{{count}} rating · {{positive}} positibo",
    noRatings: "Wala pang rating · {{positive}} positibong feedback",
    hours: "≈ {{value}} oras",
    staffValue: "≈ {{value}} na halaga ng oras ng staff",
    howCalculated: "Paano ito kinukwenta?",
    assumptionsTitle: "Mga palagay",
    assumptionsBody: "{{resolved}} chat na nasagot ng AI × ~{{minutes}} min na karaniwang oras ng staff ≈ {{hours}} oras. Halagang {{rate}}/oras.",
    assumptionsEdit: "Baguhin sa Settings → Reports.",
    noData: "—",
  },
  trend: {
    title: "Mga conversation bawat araw",
    subtitle: "{{start}} – {{end}} · ngayon kumpara sa nakaraang period",
    current: "Ngayong period",
    previous: "Nakaraan",
    emptyTitle: "Walang conversation sa period na ito",
    emptyBody: "Subukan ang mas mahabang saklaw o ibang agent o channel.",
  },
  weekly: {
    title: "Nasagot vs ipinasa",
    subtitle: "Bawat linggo",
    aiResolved: "Nasagot ng AI",
    handedOff: "Ipinasa sa staff",
    abandoned: "Iniwan",
  },
  channels: {
    title: "Hati ng channel",
    center: "conversation",
    emptyBody: "Lalabas ang hati ng channel kapag may mga conversation na.",
  },
  heatmap: {
    title: "Pinakaabalang oras",
    subtitle: "Mga conversation ayon sa araw at oras · {{timeZone}}",
    less: "Kaunti",
    more: "Marami",
    afterHours: "{{pct}} ng mga conversation ay nangyayari sa labas ng office hours.",
    days: { mon: "Lun", tue: "Mar", wed: "Miy", thu: "Huw", fri: "Biy", sat: "Sab", sun: "Lin" },
  },
  topics: {
    colTopic: "Topic",
    colConversations: "Mga conversation",
    colShare: "Bahagi",
    title: "Mga topic cluster",
    vsPrevious: "kumpara sa nakaraang period",
    seeGaps: "Tingnan ang knowledge gaps",
    uncategorized: "Walang kategorya",
    newTopic: "Bago",
    trendUp: "Tumaas ng {{value}}",
    trendDown: "Bumaba ng {{value}}",
    trendFlat: "Walang pagbabago",
    emptyTitle: "Wala pang topic",
    emptyBody: "Awtomatikong napapangkat ang mga topic habang dumarating ang mga conversation.",
  },
  languages: {
    title: "Wika",
    subtitle: "natukoy bawat mensahe",
    note: "Sumasagot ang assistant sa wika ng bisita.",
    names: { en: "English", fil: "Filipino", tl: "Filipino", taglish: "Taglish", pam: "Kapampangan", ceb: "Cebuano", unknown: "Hindi alam" },
    emptyBody: "Lalabas ang halo ng wika kapag may mga mensahe na.",
  },
  csat: {
    title: "Kasiyahan ng customer",
    collect: "Mangolekta ng rating",
    emptyTitle: "Wala pang rating",
    emptyBody: "Magdagdag ng step na “Humingi ng rating” sa isang workflow at lalabas dito ang mga rating.",
    responses: "Mga sagot",
    average: "Average na rating",
    positive: "Positibo",
    note: "Positibo ang 4 o 5 bituin, o thumbs up.",
  },
  feedback: {
    title: "Mga dahilan ng feedback",
    thumbsDown_one: "{{count}} thumbs down",
    thumbsDown_other: "{{count}} thumbs down",
    ofTotal: "{{count}} sa {{total}}",
    review: "Suriin ang negatibong feedback",
    reasons: {
      wrong_info: "Maling impormasyon",
      outdated: "Luma na ang impormasyon",
      not_what_i_asked: "Hindi nasagot ang tanong ko",
      too_long: "Masyadong mahaba / mahirap basahin",
      unspecified: "Walang ibinigay na dahilan",
    },
    emptyTitle: "Walang negatibong feedback",
    emptyBody: "Walang nag-thumbs down sa period na ito.",
  },
  quality: {
    title: "Kalidad ng sagot",
    runEvaluation: "Magpatakbo ng evaluation",
    groundedness: "Groundedness",
    groundednessHint: "Bahagi ng mga sagot na suportado ng mga nakuhang source",
    citationRate: "Citation rate",
    citationRateHint: "Bahagi ng mga sagot na may kahit isang citation",
    avgSources: "Avg na source / sagot",
    avgSourcesHint: "Karaniwang bilang ng magkakaibang source na binanggit bawat sagot",
    target: "target 1.5–3",
    noAnswer: "No-answer rate",
    noAnswerHint: "Bahagi ng mga tanong na sinagot ng fallback reply",
    footnote_one: "Batay sa {{answers}} na sagot na na-grade. Hallucination flag ngayong period: {{count}}.",
    footnote_other: "Batay sa {{answers}} na sagot na na-grade. Hallucination flag ngayong period: {{count}}.",
  },
  automation: {
    title: "Automation",
    subtitle: "Ang ginawa ng inyong mga workflow sa panahong ito.",
    open: "Buksan ang workflows",
    runs: "Mga run ng workflow",
    facingRuns_one: "{{formatted}} customer-facing",
    facingRuns_other: "{{formatted}} customer-facing",
    completion: "Pagkumpleto",
    completedOf: "{{completed}} sa {{runs}} run ang umabot sa dulo",
    engaged: "Nakipag-ugnayan",
    engagedCount_one: "{{formatted}} run ang nag-tap o sumagot",
    engagedCount_other: "{{formatted}} run ang nag-tap o sumagot",
    resolved: "Naresolba sa mga workflow",
    touched_one: "sa {{formatted}} usapang hinawakan ng workflow",
    touched_other: "sa {{formatted}} usapang hinawakan ng workflow",
    bookings: "Mga booking na natulungan",
    leads: "Mga lead na natulungan",
    assistedHint: "Matapos tumakbo ang workflow sa usapan",
    goal: "Layunin",
    goalOf: "{{met}} sa {{tracked}} run ang nakaabot sa layunin",
    failed: "Mga run na pumalya",
    failedHint: "May hakbang na pumalya",
    emptyTitle: "Walang aktibidad ng workflow sa panahong ito",
    emptyBody: "Lalabas dito ang mga run kapag nagsimula ang isang live na workflow.",
    home: {
      title: "Conversion ng Home",
      subtitle: "Mga option ng Widget Home na ipinakita, na-tap, at kung saan humantong",
      emptyTitle: "Hindi ipinakita ang Home",
      emptyBody: "I-on ang Widget Home para makita kung ilang bisita ang nag-tap ng option.",
      impressions: "Ipinakita",
      taps: "Na-tap",
      conversions: "Humantong sa usapan",
      conversion: "Conversion",
      note: "Ang usapang nabuksan sa loob ng 30 minuto mula sa isang tap ang bilang na conversion nito.",
    },
    top: {
      title: "Mga nangungunang workflow",
      workflow: "Workflow",
      completed: "Natapos",
      emptyTitle: "Walang tumakbong workflow",
      emptyBody: "Lalabas dito ang mga workflow na pinakamadalas tumakbo.",
    },
  },
  emptyCardTitle: "Walang data sa period na ito",
  loadError: "Hindi ma-load ang chart na ito.",
};
